import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "../firebase/UserProvider";
import { useTaskRole } from "../hooks/useTaskRole";
import { listTasks, listTaskUsers, subscribeToLabels } from "../firebase/tasks";
import { HomeTaskSummary } from "../components/tms/HomeTaskSummary";
import { HomeTasksRaised } from "../components/tms/HomeTasksRaised";
import { HomeTasksPending } from "../components/tms/HomeTasksPending";
import { HomeTeamWorkload } from "../components/tms/HomeTeamWorkload";
import { HomeTaskCreateForm } from "../components/tms/HomeTaskCreateForm";

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true">
      <div className="h-24 sm:h-28 bg-gray-200 rounded-2xl sm:rounded-[26px]" />
      <div className="h-64 bg-gray-200 rounded-[26px]" />
      <div className="h-64 bg-gray-200 rounded-[26px]" />
      <div className="h-64 bg-gray-200 rounded-[26px]" />
    </div>
  );
}

function getGreeting(displayName) {
  const hours = new Date().getHours();
  let salutation = "Good evening";
  if (hours < 12) {
    salutation = "Good morning";
  } else if (hours < 17) {
    salutation = "Good afternoon";
  }
  return `${salutation}, ${displayName || "User"} 👋`;
}

export function Home() {
  const { user } = useSession();
  const { isTaskUser, isTaskAdmin } = useTaskRole();
  const isTmsUser = Boolean(isTaskUser || isTaskAdmin);

  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [users, setUsers] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    onHold: 0,
    inProgress: 0,
    overdue: 0,
    raisedCount: 0,
    pendingCount: 0,
    workloadData: [],
  });

  useEffect(() => {
    if (!isTmsUser) return;
    const unsub = subscribeToLabels(setAllLabels);
    return () => unsub && unsub();
  }, [isTmsUser]);

  const fetchDashboardData = useCallback(async () => {
    if (!isTmsUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [tasks, fetchedUsers] = await Promise.all([
        listTasks(),
        listTaskUsers(),
      ]);

      setUsers(fetchedUsers || []);

      const uid = user?.uid;
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const todayStr = `${year}-${month}-${day}`;

      const countsByUid = {};
      tasks.forEach((t) => {
        if (t.status !== "Closed" && t.assigneeUid) {
          countsByUid[t.assigneeUid] = (countsByUid[t.assigneeUid] || 0) + 1;
        }
      });

      const userMap = new Map((fetchedUsers || []).map((u) => [u.uid, u]));
      const workloadData = Object.entries(countsByUid)
        .map(([assigneeUid, count]) => ({
          name: userMap.get(assigneeUid)?.name || "Unknown User",
          photoURL: userMap.get(assigneeUid)?.photoURL || "",
          count,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setStats({
        totalTasks: tasks.filter(
          (t) => t.assigneeUid === uid && t.status !== "Closed"
        ).length,
        onHold: tasks.filter(
          (t) => t.assigneeUid === uid && t.status === "On Hold"
        ).length,
        inProgress: tasks.filter(
          (t) => t.assigneeUid === uid && t.status === "In Progress"
        ).length,
        overdue: tasks.filter(
          (t) =>
            t.assigneeUid === uid &&
            t.status !== "Closed" &&
            Boolean(t.dueDate) &&
            t.dueDate < todayStr
        ).length,
        raisedCount: tasks.filter((t) => t.createdByUid === uid).length,
        pendingCount: tasks.filter(
          (t) => t.assigneeUid === uid && t.status !== "Closed"
        ).length,
        workloadData,
      });
    } catch (err) {
      // Graceful error fallback
    } finally {
      setLoading(false);
    }
  }, [isTmsUser, user?.uid]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleTaskCreated = () => {
    setIsAdding(false);
    fetchDashboardData();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container max-w-3xl mx-auto px-4 sm:px-6">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Home Page</h1>
            <p className="mt-1 mb-0 text-sm font-medium text-gray-500">
              {getGreeting(user?.displayName)}
            </p>
          </div>

          {isTmsUser && (
            <button
              type="button"
              onClick={() => setIsAdding((prev) => !prev)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors cursor-pointer shrink-0"
            >
              <span className="text-base font-bold leading-none">+</span>
              <span>Add Task</span>
            </button>
          )}
        </header>

        {isTmsUser &&
          (loading ? (
            <DashboardSkeleton />
          ) : (
            <main className="space-y-6">
              {isAdding && (
                <HomeTaskCreateForm
                  users={users}
                  allLabels={allLabels}
                  onClose={() => setIsAdding(false)}
                  onSuccess={handleTaskCreated}
                />
              )}
              <HomeTaskSummary
                totalTasks={stats.totalTasks}
                onHold={stats.onHold}
                inProgress={stats.inProgress}
                overdue={stats.overdue}
              />
              <HomeTasksRaised count={stats.raisedCount} />
              <HomeTasksPending count={stats.pendingCount} />
              <HomeTeamWorkload workloadData={stats.workloadData} />
            </main>
          ))}
      </div>
    </div>
  );
}

export default Home;
